import { type RefObject, useEffect, useRef } from "react";

/**
 * Focus trap for drawers and dialogs: remembers the trigger, moves focus in,
 * cycles Tab within the container, restores focus on unmount. Escape handling
 * lives with the caller so it can decide what "close" means for its own URL
 * state.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape?: () => void,
  /**
   * Where focus goes on close when the trigger is gone or was not focusable
   * (a row opened by mouse click, a drawer walked with prev/next).
   */
  restoreFocus?: () => HTMLElement | null,
) {
  const restoreTo = useRef<HTMLElement | null>(null);
  const onEscapeRef = useRef(onEscape);
  const restoreFocusRef = useRef(restoreFocus);

  useEffect(() => {
    restoreFocusRef.current = restoreFocus;
  }, [restoreFocus]);

  // Consumers commonly pass an inline close callback that changes identity
  // whenever a controlled form updates. Keep the latest callback without
  // re-installing the trap (and, importantly, without moving focus) on every
  // keystroke.
  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    restoreTo.current = document.activeElement as HTMLElement | null;

    const focusable = () =>
      Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null || element === document.activeElement);

    const first = focusable()[0];
    (first ?? container).focus({ preventScroll: true });

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.stopPropagation();
        onEscapeRef.current?.();
        return;
      }
      if (event.key !== "Tab") return;

      const elements = focusable();
      if (elements.length === 0) return;
      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];
      if (!firstElement || !lastElement) return;

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      const restore = restoreTo.current;
      const preferred = restoreFocusRef.current?.() ?? null;
      window.requestAnimationFrame(() => {
        const target =
          preferred?.isConnected === true
            ? preferred
            : restore?.isConnected && restore !== document.body
              ? restore
              : null;
        target?.focus({ preventScroll: false });
      });
    };
  }, [active, containerRef]);
}
